import React, { useMemo } from "react";
import { ShoppingBag, MessageCircle, Utensils, ShoppingBasket, Truck, PartyPopper, Car, RotateCw, Package } from "lucide-react";
import { resolveTenantLogoUrl } from "../../../lib/retailFormat";

// Restaurant Pilot Readiness (Mar 2026) — restaurant-mode start screen.
// Replaces the original single "Start Sales Order" CTA with a service-mode
// picker (Dine-In · To-Go · Curbside · Delivery · Catering) so the
// customer can begin ordering with one tap. Backward-compatible: when no
// modes are enabled the legacy single-CTA layout is rendered.
//
// Resume-order banner is shown above the tiles when a recent saved cart
// or customer payload is present in sessionStorage AND the cart hasn't
// expired (24h window — matches `retail/cart.py` TTL).

const MODE_VISUAL = {
  dine_in:  { icon: Utensils,       label: "Dine In",          desc: "Sit, relax, eat fresh" },
  takeout:  { icon: ShoppingBasket, label: "Take Out",         desc: "Order ahead, pick up fresh" },
  pickup:   { icon: ShoppingBasket, label: "Pickup",           desc: "Order ahead, pick up fresh" },
  curbside: { icon: Car,            label: "Curbside Pickup",  desc: "We bring it to your car" },
  delivery: { icon: Truck,          label: "Local Delivery",   desc: "We bring it to you" },
  catering: { icon: PartyPopper,    label: "Catering",         desc: "Parties, events & corporate" },
  // FedEx Parcel Shipping mode-propagation fix — this tile picker never
  // got a `shipping` entry when Parcel Shipping was added as a 5th
  // fulfillment method, so an enabled `shipping` mode fell through to
  // the generic `{icon: Utensils, label: m, desc: ""}` fallback (a
  // literal lowercase "shipping" label with the wrong icon) instead of
  // a real tile. Carrier-neutral copy by design — never say "FedEx"/
  // "USPS" here, matches RetailCatalogsAdmin.jsx's ORDER_MODE_META.
  shipping: { icon: Package,        label: "Parcel Shipping",  desc: "We ship it to your address, with tracking" },
};

const RESUME_WINDOW_MS = 24 * 60 * 60 * 1000; // 24h — matches retail/cart.py TTL

function readResumeContext(slug) {
  try {
    // Customer Identity, Address & Storefront Modal Reliability — Phase 2/7
    // fix. This used to read the UNSCOPED legacy key unconditionally,
    // silently out of sync with the tenant-scoped key `RetailCustomer.jsx`
    // has written since the cross-tenant session-leak fix — the resume
    // banner would never fire for anyone. Prefer the scoped key; fall back
    // to the legacy key only for a session written before that fix.
    const raw = (slug && sessionStorage.getItem(`retail_customer_v1:${slug}`))
      || sessionStorage.getItem("retail_customer_v1");
    if (!raw) return null;
    const c = JSON.parse(raw);
    const t = c?.last_seen_at ? new Date(c.last_seen_at).getTime() : 0;
    if (t && Date.now() - t > RESUME_WINDOW_MS) return null;
    return c;
  } catch (_e) { return null; }
}

export default function SalesOrderLanding({ tenant, catalog, slug, onStart, onChat, onStartMode, onResume, onForget, lockedModes = null }) {
  // Tenant-logo resolution (Sep 2026) — `tenant.logo_url` is stored as a
  // BACKEND-relative path (`/api/branding/files/logos/...`), so it has to
  // be resolved against the API origin before a browser on the
  // storefront origin can fetch it. See `lib/retailFormat.js::
  // resolveTenantLogoUrl` for the full root cause. One source: the same
  // saved Business Profile logo, no second field and no per-screen
  // placeholder.
  const [logoBroken, setLogoBroken] = React.useState(false);
  React.useEffect(() => { setLogoBroken(false); }, [tenant?.logo_url]);
  const logoSrc = logoBroken ? "" : resolveTenantLogoUrl(tenant?.logo_url || "");
  const features = useMemo(() => {
    const cats = (tenant?.categories_preview || []).slice(0, 4);
    if (cats.length) return cats;
    return ["Fresh menu", "Order ahead", "Made to order"];
  }, [tenant]);

  const exp = tenant?.experience_config || {};
  const status = tenant?.status || { mode: "open", message: "" };
  const isClosed = (status.mode || "open").toLowerCase() === "closed";

  // Customer Identity, Address & Storefront Modal Reliability — Phase 6.
  // Service-mode tile list — operator override (experience_config.service_modes)
  // wins when explicitly set, else strictly whichever modes the catalog
  // itself has enabled. No generic restaurant default and no automatic
  // Catering injection: Catering is a real tile only when a tenant
  // operator explicitly lists it in `service_modes`, or when the catalog
  // itself carries an enabled `catering` order mode (no such catalog-level
  // schema exists yet — see Phase 0/5 report — so today that means
  // Catering only ever appears via the explicit operator override).
  const enabledFromCatalog = Object.keys(catalog?.order_modes || {}).filter(
    (k) => (catalog.order_modes[k] || {}).enabled,
  );
  const hasOperatorOverride = Boolean(exp.service_modes && exp.service_modes.length);
  // Hospitality H1 — a hotel-referred session can only order Local
  // Delivery to the hotel, so the caller locks the tiles to that one mode.
  // `null` (every ordinary visit) leaves the configured tiles untouched.
  const finalModes = lockedModes || (hasOperatorOverride ? exp.service_modes : enabledFromCatalog);
  // Neither an operator override nor a resolved catalog with any enabled
  // mode — there is nothing honest to render as a fulfillment choice.
  const catalogUnavailable = finalModes.length === 0;

  const welcomeMsg = exp.welcome_message
    || (tenant?.welcome_quote ? `"${tenant.welcome_quote}"` : "");

  const resumeCtx = readResumeContext(slug || tenant?.chatbot_slug || tenant?.slug);
  const hasResumeData = Boolean(resumeCtx && (resumeCtx.name || resumeCtx.phone));

  return (
    <div className="min-h-screen flex items-start sm:items-center justify-center px-4 sm:px-6 py-8" data-testid="retail-start-screen">
      <div className="text-center w-full max-w-3xl">
        {/* Brand identity */}
        {logoSrc ? (
          <img
            src={logoSrc}
            alt={`${tenant?.name || catalog?.name || "Restaurant"} logo`}
            className="w-24 h-24 sm:w-32 sm:h-32 object-contain mx-auto mb-6"
            loading="lazy"
            data-testid="retail-tenant-logo"
            /* A stored logo whose file has since been removed must not
               leave a broken-image glyph on the welcome screen — fall
               back to the same placeholder an unset logo gets. */
            onError={() => setLogoBroken(true)}
          />
        ) : (
          <div
            className="w-20 h-20 sm:w-28 sm:h-28 rounded-2xl bg-black/40 grid place-items-center mx-auto mb-6 border border-emerald-200/10"
            data-testid="retail-tenant-logo-fallback"
            role="img"
            aria-label={`${tenant?.name || catalog?.name || "Restaurant"} logo`}
          >
            <ShoppingBag className="w-10 h-10 sm:w-12 sm:h-12 text-emerald-200" />
          </div>
        )}
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-emerald-50" data-testid="retail-tenant-name">
          Welcome to {tenant?.name || catalog?.name || "Our Kitchen"}
        </h1>
        {tenant?.tagline && (
          <p className="mt-2 text-[11px] sm:text-sm uppercase tracking-[0.3em] text-emerald-200/70">
            {tenant.tagline}
          </p>
        )}
        {welcomeMsg && (
          <p className="mt-4 italic text-stone-300 px-2" data-testid="retail-welcome-message">
            {welcomeMsg}
          </p>
        )}
        <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
          {features.map((f) => (
            <span key={f} className="px-3 py-1 rounded-full text-[11px] sm:text-xs bg-black/30 border border-emerald-200/15 text-emerald-100/80">
              {f}
            </span>
          ))}
        </div>

        {/* Status banner (busy / closed / pickup-only / delivery-unavailable) */}
        {status.mode && status.mode !== "open" && (
          <div
            data-testid="retail-status-banner"
            className={
              "mt-6 mx-auto max-w-2xl px-4 py-3 rounded-lg border text-sm "
              + (isClosed
                ? "bg-red-900/30 border-red-500/40 text-red-100"
                : "bg-amber-900/30 border-amber-500/40 text-amber-100")
            }
          >
            <div className="font-semibold uppercase tracking-wider text-xs">
              {isClosed ? "We're closed right now" : "Heads up"}
            </div>
            {(status.message) && <div className="mt-1 opacity-90">{status.message}</div>}
          </div>
        )}

        {/* Resume order banner */}
        {hasResumeData && !isClosed && (
          <div className="mt-6 mx-auto max-w-2xl px-4 py-3 rounded-lg bg-emerald-900/40 border border-emerald-400/40" data-testid="retail-resume-banner">
            <div className="flex items-center gap-3 flex-wrap justify-center">
              <RotateCw className="w-5 h-5 text-emerald-300" />
              <span className="text-sm text-emerald-100">
                Welcome back{resumeCtx.name ? `, ${(resumeCtx.name || "").split(" ")[0]}` : ""}. Would you like to continue your previous order?
              </span>
            </div>
            <div className="mt-3 flex gap-2 justify-center">
              <button
                type="button"
                data-testid="retail-resume-continue"
                onClick={() => (onResume ? onResume(resumeCtx) : onStart())}
                className="px-4 py-2 rounded-full bg-orange-500 text-white text-sm font-semibold hover:bg-orange-600 active:scale-[0.98] transition"
              >Continue Order</button>
              <button
                type="button"
                data-testid="retail-resume-new"
                onClick={() => (onForget ? onForget() : onStart())}
                className="px-4 py-2 rounded-full bg-black/40 border border-emerald-200/20 text-emerald-100 text-sm hover:bg-black/60 transition"
              >Start New Order</button>
            </div>
          </div>
        )}

        {/* Phase 6 — honest unavailable state instead of inventing
            fulfillment choices when the catalog can't be resolved or has
            no enabled order mode and no operator override exists. */}
        {!isClosed && catalogUnavailable && (
          <div
            className="mt-8 mx-auto max-w-2xl px-4 py-4 rounded-lg bg-amber-900/30 border border-amber-500/40 text-amber-100 text-sm"
            data-testid="retail-modes-unavailable"
          >
            <div className="font-semibold uppercase tracking-wider text-xs">Ordering isn&apos;t set up yet</div>
            <div className="mt-1 opacity-90">
              This location hasn&apos;t enabled any ordering options right now. Please check back soon
              {(tenant?.business_phone || tenant?.phone || tenant?.contact_phone)
                ? ` or call us at ${tenant.business_phone || tenant.phone || tenant.contact_phone}.`
                : "."}
            </div>
          </div>
        )}

        {/* Service-mode tiles.
            Parcel Shipping Checkout Enforcement — Issue 5. A fixed
            `grid grid-cols-2 lg:grid-cols-4` leaves unfilled grid cells
            when fewer tiles are enabled than columns, so a single tile
            (or two) renders left-aligned in row one instead of centered.
            `flex flex-wrap justify-center` centers the actual set of
            tiles regardless of count — 1, 2, or 3+ — while each tile
            keeps a fixed responsive width (2-per-row until `lg`, 4-per-
            row at `lg`+, matching the previous grid's breakpoints) so
            sizing stays consistent whether the row is full or not. */}
        {!isClosed && !catalogUnavailable && (
          <div className="mt-8 flex flex-wrap justify-center gap-3 max-w-3xl mx-auto" data-testid="retail-service-mode-grid">
            {finalModes.map((m) => {
              const v = MODE_VISUAL[m] || { icon: Utensils, label: m, desc: "" };
              const Icon = v.icon;
              const isCatering = m === "catering";
              const handle = () => {
                if (isCatering) {
                  // Sprint 1 Go-Live (Feb 2026) — Catering routing (revised).
                  // Approved priority (Feb 2026): Catering catalog is the
                  // primary acceptance path.
                  //   (1) `experience.catering_catalog_slug` set → route to that catalog.
                  //   (2) `experience.catering_packages_url` set → open in new tab (legacy).
                  //   (3) neither → alert with the business phone.
                  if (exp.catering_catalog_slug) {
                    window.location.href = `/retail/${exp.catering_catalog_slug}`;
                    return;
                  }
                  if (exp.catering_packages_url) {
                    window.open(exp.catering_packages_url, "_blank", "noopener");
                    return;
                  }
                  const phone = tenant?.business_phone || tenant?.phone || tenant?.contact_phone || "";
                  window.alert(
                    "Online Catering ordering is currently unavailable. Please contact the restaurant"
                    + (phone ? ` at ${phone}` : "") + ".",
                  );
                  return;
                }
                if (onStartMode) onStartMode(m);
                else onStart();
              };
              return (
                <button
                  key={m}
                  type="button"
                  data-testid={`retail-start-mode-${m}`}
                  onClick={handle}
                  className="group w-[calc(50%-0.375rem)] lg:w-[calc(25%-0.5625rem)] rounded-2xl border-2 border-emerald-200/20 bg-black/30 hover:bg-black/50 hover:border-orange-400/60 p-5 sm:p-6 text-left transition active:scale-[0.98] min-h-[140px] sm:min-h-[160px] flex flex-col items-start justify-between gap-3"
                >
                  <div className="w-12 h-12 rounded-xl bg-orange-500/15 grid place-items-center text-orange-300 group-hover:bg-orange-500/30 transition">
                    <Icon className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="text-base sm:text-lg font-semibold text-emerald-50">{v.label}</div>
                    {v.desc && <div className="text-[11px] sm:text-xs text-emerald-200/60 mt-0.5">{v.desc}</div>}
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* Secondary CTA — Chat or skip into menu */}
        {!isClosed && (
          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            {onChat && (
              <button
                type="button"
                onClick={onChat}
                data-testid="retail-chat-cta"
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full border-2 border-emerald-200/30 text-emerald-50 bg-black/20 hover:bg-black/40 text-sm font-semibold uppercase tracking-wider transition active:scale-[0.98]"
              >
                <MessageCircle className="w-4 h-4" /> Chat with us
              </button>
            )}
            {/* Phase 6 — "Browse menu" would otherwise fall through to
                RetailCustomer.jsx's `enabledModeList[0] || "dine_in"`
                default and silently invent a Dine-In order even though no
                mode is actually enabled. Hidden alongside the tile grid. */}
            {!catalogUnavailable && (
            <button
              type="button"
              onClick={onStart}
              data-testid="retail-tap-to-start"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-transparent border border-emerald-200/20 text-emerald-100 hover:bg-black/30 text-sm font-medium transition"
            >
              Browse menu
            </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
